import { z } from 'zod';

import { isValidPhone, normalizePhone } from '@/lib/phone';
import { CanalEnvio } from '@/types/enums';

export const telefonoContactoRowSchema = z.object({
  numero: z.string(),
  nombre: z.string().trim().max(100, 'Máximo 100 caracteres').optional().or(z.literal('')),
});

export type TelefonoContactoFormRow = z.infer<typeof telefonoContactoRowSchema>;

const telefonosContactoSchema = z
  .array(telefonoContactoRowSchema)
  .default([])
  .superRefine((items, ctx) => {
    const seen: string[] = [];
    items.forEach((item, index) => {
      if (!item.numero) return;
      if (!isValidPhone(item.numero)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Número inválido. Usá formato internacional.',
          path: [index, 'numero'],
        });
        return;
      }
      const normalized = normalizePhone(item.numero);
      if (normalized && seen.includes(normalized)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Este número ya está en la lista.',
          path: [index, 'numero'],
        });
      } else if (normalized) {
        seen.push(normalized);
      }
    });
  });

const facturacionSchema = z
  .object({
    cuit: z.string().trim().max(20).optional().or(z.literal('')),
    razon_social: z.string().trim().max(200).optional().or(z.literal('')),
    email_facturacion: z
      .string()
      .trim()
      .email({ message: 'Email inválido' })
      .optional()
      .or(z.literal('')),
    telefono_whatsapp_facturacion: z.string().trim().optional().or(z.literal('')),
    envio_factura: z.nativeEnum(CanalEnvio).optional().or(z.literal('')),
  })
  .refine(
    (data) =>
      data.envio_factura !== CanalEnvio.EMAIL ||
      (data.email_facturacion && data.email_facturacion.trim().length > 0),
    {
      message: 'Si elegís email para facturación, ingresá el email',
      path: ['email_facturacion'],
    }
  )
  .refine(
    (data) =>
      data.envio_factura !== CanalEnvio.WHATSAPP ||
      (data.telefono_whatsapp_facturacion &&
        data.telefono_whatsapp_facturacion.trim().length > 0),
    {
      message: 'Si elegís WhatsApp para facturación, ingresá el teléfono',
      path: ['telefono_whatsapp_facturacion'],
    }
  )
  .refine(
    (data) => !data.telefono_whatsapp_facturacion || isValidPhone(data.telefono_whatsapp_facturacion),
    {
      message: 'Número inválido. Usá formato internacional.',
      path: ['telefono_whatsapp_facturacion'],
    }
  );

export const establecimientoFormSchema = z.object({
  nombre: z
    .string()
    .trim()
    .min(1, 'Ingresá el nombre del establecimiento')
    .max(200, 'Máximo 200 caracteres'),
  firma_ganaderos: z.string().trim().max(500).optional().or(z.literal('')),
  localidad: z.string().trim().max(100).optional().or(z.literal('')),
  uid_profesional_actual: z.string().min(1, 'Seleccioná un profesional asignado'),
  facturacion: facturacionSchema,
  telefonos_contacto: telefonosContactoSchema,
});

/** Variante para edición: NO incluye uid_profesional_actual (se cambia desde el dialog). */
export const establecimientoUpdateFormSchema = z.object({
  nombre: z.string().trim().min(1, 'Ingresá el nombre del establecimiento').max(200),
  firma_ganaderos: z.string().trim().max(500).optional().or(z.literal('')),
  localidad: z.string().trim().max(100).optional().or(z.literal('')),
  facturacion: facturacionSchema,
  telefonos_contacto: telefonosContactoSchema,
});

export type EstablecimientoFormValues = z.infer<typeof establecimientoFormSchema>;
export type EstablecimientoUpdateFormValues = z.infer<typeof establecimientoUpdateFormSchema>;
